import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import i18n from '@/i18n';
import { hasPermission, ActionsEnum } from '@/lib/permissions';
import { dashboardRoutePaths, setupRoutePath } from '@/routes/route-config';
import { rememberReturnTo } from '@/lib/session';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireClinic?: boolean;
}

export const ProtectedRoute = ({
  children,
  requireClinic = false,
}: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading, clinic, user, permissions } = useAuth();
  const location = useLocation();

  const storedClinicId = localStorage.getItem('selectedClinicId');
  console.log('🔒 ProtectedRoute:', {
    path: location.pathname,
    isAuthenticated,
    isLoading,
    hasClinicInState: !!clinic,
    hasClinicInStorage: !!storedClinicId,
    requireClinic,
    isEmailVerified: user?.isEmailVerified,
  });

  // Show loading spinner while checking authentication
  if (isLoading) {
    console.log('⏳ ProtectedRoute: Still loading...');
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-100 flex flex-col items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary-500 mb-4" />
        <p className="text-neutral-600">{i18n.t('common.loading')}</p>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    console.log('❌ ProtectedRoute: Not authenticated, redirecting to login');
    rememberReturnTo(location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }

  // If on verify-email but already verified, redirect to select-clinic
  if (location.pathname === '/verify-email' && user?.isEmailVerified === true) {
    console.log(
      '✅ ProtectedRoute: Email already verified, redirecting away from verify-email',
    );
    return <Navigate to="/select-clinic" replace />;
  }

  // Check email verification for all protected routes except /verify-email
  if (
    location.pathname !== '/verify-email' &&
    user &&
    user.isEmailVerified === false
  ) {
    console.log(
      '📧 ProtectedRoute: Email not verified, redirecting to verify-email',
    );
    return (
      <Navigate
        to="/verify-email"
        state={{ email: user.email, autoSend: false }}
        replace
      />
    );
  }

  // Block deactivated staff from protected pages
  if (user?.isActive === false && location.pathname !== '/deactivated') {
    console.log(
      '🚫 ProtectedRoute: Staff deactivated, redirecting to /deactivated',
    );
    return <Navigate to="/deactivated" replace />;
  }

  // If clinic is required but not selected, redirect to clinic selection
  // (except if already on verify-email or select-clinic pages)
  if (requireClinic) {
    // Check both state and localStorage (state might not be updated yet due to React's async updates)
    const hasClinic = clinic || storedClinicId;

    if (!hasClinic) {
      const allowedPaths = ['/verify-email', '/select-clinic'];
      if (!allowedPaths.includes(location.pathname)) {
        console.log('🔀 ProtectedRoute: No clinic, redirecting to selection');
        rememberReturnTo(location.pathname + location.search);
        return <Navigate to="/select-clinic" replace />;
      }
    }
  }

  /*
    First-run setup. A clinic whose setup is open sends whoever can manage
    it to /setup, from any page, until the clinic can take bookings. Staff
    without that right are let in: they cannot finish setup, and keeping
    them out would only lock the door on the people waiting for it.

    `=== null` on purpose: a payload from an API older than the column has
    no key at all, and that clinic is a working one.
  */
  if (requireClinic && clinic) {
    const setupOpen = clinic.setupCompletedAt === null;
    const mayRunSetup = hasPermission(
      permissions,
      'clinic',
      ActionsEnum.MANAGE,
    );
    const onSetup = location.pathname === setupRoutePath;
    if (setupOpen && mayRunSetup && !onSetup) {
      return <Navigate to={setupRoutePath} replace />;
    }
    if (onSetup && (!setupOpen || !mayRunSetup)) {
      return <Navigate to={dashboardRoutePaths.home} replace />;
    }
  }

  console.log('✅ ProtectedRoute: Rendering protected page');
  return <>{children}</>;
};
